<script>
    import Vue from 'vue'
    import {Client as ColyseusClient} from 'colyseus.js'
    import {$eBus} from '@/event-bus/index'
    import GameClientEvent from '@/enum/GameClientEvent'
    import {ConsoleLogger} from '@/console-logger'
    import AppConfig from '@/app-config'

    export default {
        name: 'sgi-game-client',
        data() {
            return {
                client: null,
                room: null,
                roomId: null,
                customId: null,

                lastConnectedRoomId: null,
                lastConnectedOptions: {},

                playerSessionId: false, // set in this.room.onJoin
                logger: new ConsoleLogger('SgiGameClient', 'background: #00aa00; color: white', null),

                isConnected: false,
                phase: null,
                colyseusUrl: null,
            }
        },
        mounted() {
            if (AppConfig.debug.gameClient) {
                this.logger.enable();
            }

            let previousGameClient = this.$gameClient || null;

            Object.defineProperty(Vue.prototype, '$gameClient', {
                value: this,
                configurable: true
            });

            $eBus.$emit(GameClientEvent.Mounted, this, previousGameClient);

            $eBus.$on(GameClientEvent.Open, this.onClientOpen);
            $eBus.$on(GameClientEvent.Close, this.onClientClose);

            this.logMessage(`Assigned to Vue.$gameClient`)
        },
        beforeDestroy() {
            $eBus.$off(GameClientEvent.Open, this.onClientOpen);
            $eBus.$off(GameClientEvent.Close, this.onClientClose);
        },
        watch: {
            roomId() {
                $eBus.$emit(GameClientEvent.CustomRoomIdChanged, this.roomId)
            }
        },
        computed: {
            state() {
                try {
                    return this.state || this.room.serializer.state || {};
                } catch (error) {
                    return {};
                }
            },
            inRoom() {
                try {
                    return !!this.room
                } catch (error) {
                    return null
                }
            },
        },
        methods: {
            // Various helpers
            emitPhaseChange() {
                this.logMessage(`Phase changed! New phase: ${this.phase}`);

                $eBus.$emit(GameClientEvent.PhaseChanged, this.phase)
            },
            getLastConnectedOptions() {
                return this.lastConnectedOptions || {};
            },
            getLastConnectedRoomId() {
                return this.lastConnectedRoomId || null;
            },
            canReconnect() {
                return !!this.lastConnectedRoomId;
            },
            reconnect() {
                if (!this.canReconnect()) {
                    throw new Error('Cannot reconnect because last connected room id is empty');
                }

                console.log('Disconnecting..');

                if (this.isConnected) {
                    this.disconnect();
                } else {
                    this.reset();
                }

                console.log('Waiting 5 seconds to join again..');

                return new Promise((resolve, reject) => {
                    this.$nextTick(() => {
                        console.log('Joining again!');

                        let joinResult = this.joinRoomByColyseusId(this.lastConnectedRoomId, this.lastConnectedOptions);

                        resolve(joinResult);
                    });
                });
            },
            isPhase(phase) {
                if (Array.isArray(phase)) {
                    return phase.includes(this.phase)
                } else {
                    return phase === this.phase
                }
            },

            isMe(playerId) {
                return playerId === this.playerSessionId
            },

            onClientOpen() {
                this.logMessage('onClientOpen');

                this.isConnected = true;
            },
            onClientClose() {
                this.logMessage('onClientClose');

                this.isConnected = false;
            },

            // Logger
            clearLog() {
                console.log('Clearing log!')

                this.logMessages = []
            },
            logMessage(message) {
                this.logger.logMessage(message)
            },

            // Setup
            setUrlFromHostPort(host, port, secure) {
                let colyseusHostWithPort = `${host}:${port}`;
                let colyseusUrl = (secure ? 'wss' : 'ws') + '://' + colyseusHostWithPort;

                this.setUrl(colyseusUrl);
            },
            setUrl(colyseusUrl) {
                this.logMessage(`Set colyseus WS url to ${colyseusUrl}`);

                this.colyseusUrl = colyseusUrl;

                this.client = new ColyseusClient(colyseusUrl);
            },

	        handleRoomJoinPromise(promise) {
                this.phase = null;
                this.roomId = null;
                this.lastConnectedRoomId = null;

            	return promise.then(room => {
					this.roomId = room.id;
					this.lastConnectedRoomId = room.id;

					console.log(room);
					console.log(`Joined room with id ${room.id}`);

					$eBus.$emit(GameClientEvent.Open);

					this.room = room;

					this.room.onMessage((message) => {
						this.logMessage(GameClientEvent.RoomMessage + ': ' + message);

						$eBus.$emit(GameClientEvent.RoomMessage, this.room, message)
					});

					this.room.onLeave((code) => {
						this.logMessage(GameClientEvent.RoomLeave + ': ' + code);

						$eBus.$emit(GameClientEvent.RoomLeave, this.room, code)
					});

					this.room.onStateChange((state) => {
						this.logMessage(GameClientEvent.RoomStateChange);

						if (state.phase) {
							if (this.phase !== state.phase) {
                                this.phase = state.phase;

                                this.emitPhaseChange();
                            }
						}

						if (state.customId) {
							this.customId = state.customId;

							console.log('CUSTOM CUSTOM CUSTOM ID', this.customId);

                            $eBus.$emit(GameClientEvent.CustomRoomIdChanged, this.customId);
						}

						$eBus.$emit(GameClientEvent.RoomStateChange, this.room, state);
					});

					this.logMessage(GameClientEvent.RoomJoin);

					$eBus.$emit(GameClientEvent.RoomJoin, this.room);

					return room;
				}).catch(error => {
					this.logMessage(GameClientEvent.Error + ': ' + error)

					$eBus.$emit(GameClientEvent.Error, error)

					throw error;
				});
	        },

            getJoinOptions(options, customId) {
                if (typeof options === 'undefined' || !options) {
                    options = {};
                }

                if (customId && !options.hasOwnProperty('customId')) {
                    options.customId = customId;
                }

                if (!options.hasOwnProperty('userAgent') && navigator.userAgent) {
                    options.userAgent = navigator.userAgent;
                }

                return options;
            },

            // Join or create rooms
            connectRoom(method, customId, options = {}) {
                options = this.getJoinOptions(options, customId);

                this.logMessage(`Connecting to room with method ${method} and customId ${options.customId}`);

                this.lastConnectedOptions = options;

                options = this.getJoinOptions(options);

                return this.handleRoomJoinPromise(this.client[method]('game', options));
            },

            joinOrCreateRoom(customId, options = {}) {
                this.logMessage(`Creating (or joining) room with custom id ${customId}`);

                return this.connectRoom('joinOrCreate', customId, options).then(room => {
                    console.log('Created/joined successfully!');

                    return room;
                }).catch(error => {
                    console.log('Could not create/join room!');

                    throw error;
                });
            },
            createRoom(customId, options = {}) {
                this.logMessage(`Creating room with custom id ${customId}`);

                return this.connectRoom('create', customId, options).then(room => {
                    console.log('Created successfully!');
                }).catch(error => {
                    console.log('Could not create room!');

                    throw error;
                });
            },
			joinRoom(customId, options = {}) {
				this.logMessage(`Joining room with custom id ${customId}`);

				return this.client.getAvailableRooms("game").then(rooms => {
					for (let room of rooms) {
						if (room.metadata.customId === customId) {
							this.logMessage(`Found available room with room id ${room.roomId}`);

							return this.joinRoomByColyseusId(room.roomId, options);
						}
					}

					throw new Error(`Did not find room with customId ${customId}`);
				});
			},
			joinRoomByColyseusId(roomId, options = {}) {
				this.logMessage(`Joining room with colyseus id ${roomId}`);

				options = this.getJoinOptions(options);

				this.lastConnectedOptions = options;

				return this.handleRoomJoinPromise(this.client.joinById(roomId, options));
			},

            // Close and leave room
            leaveRoom() {
                if (this.room) {
                    try {
                        this.logMessage('Leaving room..');

                        this.room.leave();
                    } catch (err) {
                        // Whatever.. guess we've already left
                    }

                    this.room = null
                }
            },
            disconnect() {
                this.leaveRoom();

                if (this.client) {
                    console.log(this.client);
                }

                this.reset();
            },
            reset() {
                this.phase = null;
                this.isConnected = false;
                this.room = null;
                this.roomId = null;
                this.customId = null;

                $eBus.$emit(GameClientEvent.CustomRoomIdChanged, null);
            },

            // Sending of data
            send(data) {
                this.logMessage('Sending data');
                this.logMessage(JSON.stringify(data, null, 2));

                this.room.send(data)
            }
        },

        // without this Vue will complain
        // "vue.esm.js:628 [Vue warn]: Failed to mount component: template or render function not defined."
        render: h => h(),
    }
</script>